// Listing page title and meta description for search results (BSC-22, built in BSC-4).
//
// Title:       {listing title} | {brand} {gender} {category} size {size} | BUYSELL Vintage
// Description: {condition} {brand} {gender} {category}, size {size}. {screened seller text}
//
// Missing attributes are skipped without leaving empty separators, and an attribute already
// written in the listing title is not repeated. The seller's text is used only when it passes
// the screen below; otherwise the description is built from the attributes alone.
import { englishDataset, englishRecommendedTransformers, RegExpMatcher } from 'obscenity';
import { convertMoneyToNumber } from './currency';
import {
  brandLabel,
  categoryLabel,
  conditionLabel,
  genderLabel,
  listingStillImageUrls,
  schemaItemCondition,
  sizePhrase,
} from './listingAttributes';

export const TITLE_MAX_LENGTH = 60;
export const DESCRIPTION_MAX_LENGTH = 155;

// Most words of the listing title worth giving up to keep the attribute part in the title
const MAX_TITLE_WORDS_DROPPED = 2;

// Words the profanity list flags that are ordinary in resale listings
const ALLOWED_TERMS = ['dickies', 'assortment', 'pussycat dolls', 'cum laude'];

// Contact details and ways to take the sale off the marketplace
const CONTACT_PATTERNS = [
  /[^\s@]+@[^\s@]+\.[a-z]{2,}/i, // email
  /\bhttps?:\/\/|\bwww\./i, // web link
  /\b[a-z0-9-]+\.(com|net|org|io|co|us|shop|store|me|app|ly|gg|info|biz|link)\b/i, // bare domain
  /(?:\+?\d[\s().-]*){10,}/, // phone number, 10 or more digits
  /(^|[\s(])@[a-z0-9_.]{2,}/i, // social handle
  /\b(ig|insta|snap)\s*[:@]/i,
  /\b(instagram|snapchat|whatsapp|telegram|venmo|cash\s?app|zelle|paypal)\b/i,
];

let profanityMatcher = null;
const getProfanityMatcher = () => {
  if (!profanityMatcher) {
    const dataset = englishDataset.build();
    profanityMatcher = new RegExpMatcher({
      ...dataset,
      ...englishRecommendedTransformers,
      whitelistedTerms: [...(dataset.whitelistedTerms || []), ...ALLOWED_TERMS],
    });
  }
  return profanityMatcher;
};

const normaliseSpace = text => (typeof text === 'string' ? text.replace(/\s+/g, ' ').trim() : '');

/**
 * The seller's text, whitespace collapsed, or null when it is empty or carries contact details
 * or a word on the profanity list.
 */
export const screenSellerText = text => {
  const normalised = normaliseSpace(text);
  if (!normalised) {
    return null;
  }
  if (CONTACT_PATTERNS.some(pattern => pattern.test(normalised))) {
    return null;
  }
  return getProfanityMatcher().hasMatch(normalised) ? null : normalised;
};

const escapeRegExp = text => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Whether every word of the phrase already appears, as a whole word, in the text
const containsWords = (text, phrase) =>
  phrase
    .split(' ')
    .every(word => new RegExp(`(^|[^\\w])${escapeRegExp(word)}($|[^\\w])`, 'i').test(text));

const startsWithWord = (text, word) =>
  new RegExp(`^${escapeRegExp(word)}($|[^\\w])`, 'i').test(text);

/**
 * Brand, gender, category and size phrase for a listing, null where missing. Gender is skipped
 * when the category label already starts with it, and any attribute already written in the
 * listing title is skipped.
 */
const attributeParts = (publicData, listingTitle = '') => {
  const category = categoryLabel(publicData);
  const gender = genderLabel(publicData);
  const genderMaybe = gender && !(category && startsWithWord(category, gender)) ? gender : null;
  const notInTitle = word => (word && !containsWords(listingTitle, word) ? word : null);
  return {
    brand: notInTitle(brandLabel(publicData)),
    gender: notInTitle(genderMaybe),
    category: notInTitle(category),
    size: notInTitle(sizePhrase(publicData)),
  };
};

// Order the attributes are given up in when a title is too long: least useful to searchers first
const TITLE_DROP_ORDER = ['gender', 'size', 'category', 'brand'];

// Cut at a word boundary to at most maxLength, never inside a word. A first word longer than
// maxLength is kept whole. Trailing separators are dropped.
const trimToWords = (text, maxLength) => {
  if (text.length <= maxLength) {
    return text;
  }
  const words = text.split(' ');
  let result = words[0];
  for (let i = 1; i < words.length; i++) {
    const next = `${result} ${words[i]}`;
    if (next.length > maxLength) {
      break;
    }
    result = next;
  }
  return result.replace(/[\s,;:|/&+-]+$/, '');
};

/**
 * Title tag for a listing page, about 60 characters, never cutting a word.
 */
export const listingMetaTitle = ({ title, publicData, siteTitle }) => {
  const listingTitle = normaliseSpace(title);
  const parts = attributeParts(publicData, listingTitle);
  const suffix = ` | ${siteTitle}`;
  const join = (...segments) => `${segments.filter(Boolean).join(' | ')}${suffix}`;
  const attributesText = ({ brand, gender, category, size }) =>
    [brand, gender, category, size].filter(Boolean).join(' ');

  if (!listingTitle) {
    return join(attributesText(parts));
  }

  // Attribute sets from all of them down to none, giving up the least useful first
  const stages = TITLE_DROP_ORDER.reduce(
    (acc, key) => [...acc, { ...acc[acc.length - 1], [key]: null }],
    [parts]
  ).map(attributesText);
  const budgetFor = attributes =>
    TITLE_MAX_LENGTH - suffix.length - (attributes ? attributes.length + 3 : 0);

  // Keep the listing title whole, giving up as many attributes as that takes
  const whole = stages.find(attributes => listingTitle.length <= budgetFor(attributes));
  if (whole != null) {
    return join(listingTitle, whole);
  }
  // Otherwise trim the listing title, keeping attributes only while that costs it at most
  // MAX_TITLE_WORDS_DROPPED words. The seller's words tell similar listings apart: cutting more
  // of them for the attributes gave different listings the same title (BSC-22).
  const titleWords = listingTitle.split(' ').length;
  const trimmed = stages.find(
    attributes =>
      !attributes ||
      titleWords - trimToWords(listingTitle, budgetFor(attributes)).split(' ').length <=
        MAX_TITLE_WORDS_DROPPED
  );
  return join(trimToWords(listingTitle, budgetFor(trimmed)), trimmed);
};

/**
 * Meta description for a listing page, under 155 characters, or null when the listing has
 * neither attributes nor usable seller text (the page then uses the site description).
 */
export const listingMetaDescription = ({ description, publicData }) => {
  const { brand, gender, category, size } = attributeParts(publicData);
  const item = [conditionLabel(publicData), brand, gender, category].filter(Boolean).join(' ');
  const summary = [item, size].filter(Boolean).join(', ');
  const sellerText = screenSellerText(description);
  const text = [summary && `${summary}.`, sellerText].filter(Boolean).join(' ');
  if (!text) {
    return null;
  }
  if (text.length < DESCRIPTION_MAX_LENGTH) {
    return text;
  }
  return `${trimToWords(text, DESCRIPTION_MAX_LENGTH - 2).replace(/[.!?]+$/, '')}…`;
};

/**
 * Product schema for a listing page (BSC-23, built in BSC-4). The marketplace is named as the
 * seller, never the individual seller. Shipping and returns are left out: listings carry no
 * shipping or returns terms to publish.
 *
 * @param {Object} args
 * @param {String} args.listingId listing uuid, published as sku
 * @param {String} args.title listing title, published as name
 * @param {String} args.description meta description from listingMetaDescription, or null
 * @param {Object} args.publicData listing public data
 * @param {Money} args.price seller's asking price, nothing added
 * @param {String} args.availability schema.org availability URL
 * @param {String} args.url canonical listing URL
 * @param {String} args.siteTitle marketplace name
 * @param {String} args.organizationId @id of the Organization block Page renders
 */
export const listingProductSchema = ({
  listingId,
  title,
  description,
  publicData,
  price,
  availability,
  url,
  siteTitle,
  organizationId,
}) => {
  const brand = brandLabel(publicData);
  const itemCondition = schemaItemCondition(publicData);
  const images = listingStillImageUrls(publicData);
  // A plain decimal with a dot: schema.org rejects grouped numbers such as 1,234.00
  const priceMaybe = price ? { price: convertMoneyToNumber(price).toFixed(2) } : {};
  return {
    '@context': 'http://schema.org',
    '@type': 'Product',
    name: normaliseSpace(title),
    ...(description ? { description } : {}),
    ...(images.length > 0 ? { image: images } : {}),
    ...(brand ? { brand: { '@type': 'Brand', name: brand } } : {}),
    sku: listingId,
    url,
    offers: {
      '@type': 'Offer',
      url,
      priceCurrency: price?.currency || 'USD',
      ...priceMaybe,
      availability,
      ...(itemCondition ? { itemCondition } : {}),
      seller: { '@type': 'Organization', '@id': organizationId, name: siteTitle },
    },
  };
};
